import { CustomFile } from "../../../Helpers/file";
import { uuid } from "../../../../../Common/UUID";
import { ICandyApiDraftClient } from "../../../../../Common/CandyApiClient/ICandyApiClient";
import { FormStore } from "../../Core/Store/CandyStore";
import { IFormModelAccessor } from "../../Form/IFormModelAccessor";
import { TaskStorage } from "../Attachments/Tasks/TaskStorage";
import { UploadToDriveTask } from "../Attachments/Tasks/UploadToDriveTask";
import {
    ExcelsToPartialFufsResponse,
    FileProperties,
    UploadFilePropertiesResponse,
} from "../../../../../Common/CandyApiClient/Types/DraftActions/FileProperties";
import {
    CombinatorType,
    CombineFufResult,
    FufCombineMethod,
} from "../../../../../Common/CandyApiClient/Types/DraftActions/CombineFufResult";

import { IFufManager, UploadFileOptions, UploadFormTypes } from "./IFufManager";

export class FufManager implements IFufManager {
    private readonly store: FormStore;
    private readonly modelAccessor: IFormModelAccessor;
    private readonly candyApiDraftClient: ICandyApiDraftClient;
    private readonly taskStorage: TaskStorage;

    public constructor(store: FormStore, candyApiDraftClient: ICandyApiDraftClient, modelAccessor: IFormModelAccessor) {
        this.modelAccessor = modelAccessor;
        this.candyApiDraftClient = candyApiDraftClient;
        this.store = store;
        this.taskStorage = new TaskStorage();
    }

    public async setFuf(driveUri: string): Promise<void> {
        await this.candyApiDraftClient.setFufByDriveUri(driveUri);
    }

    public async excelsToPartialFufs(
        gfv: string,
        pathToTable: string,
        driveUris: string[]
    ): Promise<ExcelsToPartialFufsResponse | undefined> {
        return await this.candyApiDraftClient.excelsToPartialFufs(gfv, pathToTable, driveUris);
    }

    public async importXmls(
        driveUris: string[],
        combineMethod: FufCombineMethod,
        combinatorType: CombinatorType,
        isImportByFuf: boolean,
        args?: string[]
    ): Promise<CombineFufResult | undefined> {
        return await this.candyApiDraftClient.importXmls(driveUris, combineMethod, combinatorType, isImportByFuf, args);
    }

    public async addFile(
        file: CustomFile,
        ignoreSizeCheck?: boolean,
        skipUploadFile?: boolean,
        uploadFileOptions?: UploadFileOptions
    ): Promise<FileProperties | undefined> {
        let driveUri: string | undefined;

        if (skipUploadFile) {
            driveUri = file.contentUri;
        } else {
            const fileInfo = this.createFileInfo(file);
            driveUri = await this.tryUploadToDrive(fileInfo);
        }

        if (driveUri == undefined) {
            throw new Error("driveUri undefined");
        }

        if (uploadFileOptions?.formType === UploadFormTypes.NDS) {
            const filePropertiesList = await this.candyApiDraftClient.getFileNdsProperties({
                driveUri: driveUri,
                ignoreSizeCheck: ignoreSizeCheck,
                requestedGfv: uploadFileOptions.requestedGfv,
                declarationGfv: uploadFileOptions.declarationGfv,
            });

            if (filePropertiesList?.length === 1) {
                return filePropertiesList[0];
            }

            return filePropertiesList?.find?.(item => item.gfv === uploadFileOptions.requestedGfv);
        } else if (uploadFileOptions?.formType === UploadFormTypes.default && uploadFileOptions.requestedGfv) {
            return await this.candyApiDraftClient.prepareAndGetFileProperties(
                driveUri,
                uploadFileOptions.requestedGfv,
                ignoreSizeCheck
            );
        }

        const fileProperties = await this.candyApiDraftClient.getFileProperties(driveUri, ignoreSizeCheck);

        return fileProperties;
    }

    public async prepareAndAddFile(file: CustomFile, gfv: string): Promise<FileProperties | undefined> {
        const fileInfo = this.createFileInfo(file);

        const driveUri = await this.tryUploadToDrive(fileInfo);

        if (driveUri == undefined) {
            throw new Error("driveUri undefined");
        }
        const fileProperties = await this.candyApiDraftClient.prepareAndGetFileProperties(driveUri, gfv);

        return fileProperties;
    }

    public async uploadFilesToPatchFuf(
        driveUris: string[],
        gfv: string
    ): Promise<UploadFilePropertiesResponse | undefined> {
        const fileProperties = await this.candyApiDraftClient.uploadAndGetFileProperties(driveUris, gfv);

        return fileProperties;
    }

    public async getFufExpectedDemandFileName(): Promise<string | undefined> {
        const inner = await this.candyApiDraftClient.getInner(["@draftMeta/demandfilename.value"]);
        const demandFileName = inner["@draftMeta/demandfilename"]?.value;
        return demandFileName;
    }

    private async tryUploadToDrive(fileInfo: FileInfo): Promise<string | undefined> {
        if (fileInfo.contentUri) {
            return undefined;
        }
        const task = this.taskStorage.getTask(fileInfo.id);
        if (task) {
            await task.taskPromise;
            return undefined;
        }

        let driveUri: string | undefined = undefined;

        const uploadTask = new UploadToDriveTask(
            this.store,
            this.modelAccessor,
            fileInfo.id,
            "",
            fileInfo.file,
            false,
            async (contentUri: string | undefined) => {
                driveUri = contentUri;
            }
        );
        this.taskStorage.setTask(fileInfo.id, uploadTask);
        await uploadTask.taskPromise;
        this.taskStorage.deleteTask(fileInfo.id);

        return driveUri;
    }

    private createFileInfo(file: CustomFile): FileInfo {
        const id = uuid();
        return {
            id: id,
            contentUri: file.contentUri,
            file: file,
            meta: {
                fileName: file.name,
                fileSize: file.size,
                progress: file.contentUri ? 100 : 0,
            },
        };
    }
}

export interface FileInfo {
    id: string;
    file: CustomFile;
    contentUri?: string;
    meta: {
        fileName: string;
        fileSize: number;
        progress: number | string;
        isConverted?: boolean;
    };
}
